import { Field, NumberInput as ChakraNumberInput } from "@chakra-ui/react";
import { forwardRef } from "react";
import { getThemeColor } from "theme/colors";

interface NumberInputProps extends Omit<
  React.ComponentProps<typeof ChakraNumberInput.Root>,
  "value" | "onValueChange"
> {
  label?: string;
  helperText?: string;
  errorText?: string;
  required?: boolean;
  value?: number;
  onValueChange?: (value: number) => void;
  min?: number;
  max?: number;
}

export const NumberInput = forwardRef<HTMLDivElement, NumberInputProps>(
  (
    {
      label,
      helperText,
      errorText,
      required,
      value,
      onValueChange,
      min,
      max,
      ...props
    },
    ref,
  ) => {
    const inputStyles = {
      bg: getThemeColor("input.bg"),
      borderColor: getThemeColor("input.border"),
      color: getThemeColor("input.text"),
      _focus: {
        borderColor: getThemeColor("input.borderFocus"),
        boxShadow: `0 0 0 1px ${getThemeColor("input.borderFocus").base}`,
        _dark: {
          boxShadow: `0 0 0 1px ${getThemeColor("input.borderFocus")._dark}`,
        },
      },
    };

    const content = (
      <ChakraNumberInput.Root
        ref={ref}
        value={value?.toString()}
        onValueChange={(details: any) => onValueChange?.(details.valueAsNumber)}
        min={min}
        max={max}
        {...props}
      >
        <ChakraNumberInput.Input {...inputStyles} />
        <ChakraNumberInput.Control>
          <ChakraNumberInput.IncrementTrigger />
          <ChakraNumberInput.DecrementTrigger />
        </ChakraNumberInput.Control>
      </ChakraNumberInput.Root>
    );

    if (label || helperText || errorText) {
      return (
        <Field.Root invalid={!!errorText} required={required}>
          {label && <Field.Label>{label}</Field.Label>}
          {content}
          {helperText && <Field.HelperText>{helperText}</Field.HelperText>}
          {errorText && <Field.ErrorText>{errorText}</Field.ErrorText>}
        </Field.Root>
      );
    }

    return content;
  },
);

NumberInput.displayName = "NumberInput";
